import type { TranslationKey } from '@dreamgame/shared';
import { signal } from '@preact/signals';
import { tv } from 'tailwind-variants';
import type { PawnData } from '../../state/entities';
import { t } from '../../state/i18n';
import {
  COMBAT_STANCE_INFO,
  CombatStance,
  type CombatStanceType,
  selectedPawnCombatStance,
  setCombatStance,
} from '../../state/pawnGear';
import { selectedIds } from '../../state/selection';

const combatPanel = tv({
  slots: {
    root: 'flex flex-col',
    title: [
      'text-center text-rs-gold font-semibold text-xs mb-3 tracking-wide',
      'border-b border-rs-stone-light/30 pb-2',
    ],
    section: 'mb-3',
    sectionLabel: ['text-[9px] uppercase tracking-wider text-rs-parchment-dark mb-2'],
    draftButton: [
      'w-full py-2.5 px-3',
      'rounded',
      'text-xs font-semibold uppercase tracking-wide',
      'transition-all duration-150',
      'cursor-pointer',
      'flex items-center justify-center gap-2',
    ],
    draftedStyle: [
      'bg-gradient-to-b from-red-700 to-red-900',
      'border border-red-500/50',
      'text-red-100',
      'hover:from-red-600 hover:to-red-800',
    ],
    undraftedStyle: [
      'bg-gradient-to-b from-rs-stone to-rs-stone-dark',
      'border border-rs-stone-light/50',
      'text-rs-parchment',
      'hover:border-rs-gold/40',
    ],
    stanceSection: ['mt-3 pt-3', 'border-t border-rs-stone-light/20'],
    stanceOption: [
      'flex items-center gap-2 py-2 px-2',
      'bg-rs-stone-dark/40 rounded mb-1.5',
      'cursor-pointer',
      'hover:bg-rs-stone-dark/60',
      'transition-colors',
    ],
    stanceActive: ['bg-rs-stone-dark/80 border border-rs-gold/30'],
    stanceRadio: [
      'w-3 h-3 rounded-full',
      'border border-rs-stone-light/50',
      'flex items-center justify-center',
    ],
    stanceRadioActive: ['border-rs-gold bg-rs-gold/20'],
    stanceDot: ['w-1.5 h-1.5 rounded-full bg-rs-gold'],
    stanceInfo: 'flex-1',
    stanceName: ['text-[11px] text-rs-parchment font-medium'],
    stanceDesc: ['text-[9px] text-rs-parchment-dark'],
    responseSection: ['mt-3 pt-3', 'border-t border-rs-stone-light/20'],
    responseOption: [
      'flex items-center gap-2 py-1.5 px-2',
      'bg-rs-stone-dark/40 rounded mb-1',
      'cursor-pointer',
      'hover:bg-rs-stone-dark/60',
      'transition-colors',
    ],
    responseActive: ['bg-rs-stone-dark/80 border border-rs-gold/30'],
    responseLabel: ['text-[10px] text-rs-parchment'],
    statsSection: ['mt-3 pt-3', 'border-t border-rs-stone-light/20'],
    statsRow: ['flex justify-between text-[10px] py-1'],
    statsLabel: 'text-rs-parchment-dark',
    statsValue: 'text-rs-parchment',
  },
});

// Behavior when threatened
const ThreatResponse = {
  FLEE: 'flee',
  FIGHT: 'fight',
  IGNORE: 'ignore',
} as const;

type ThreatResponseType = (typeof ThreatResponse)[keyof typeof ThreatResponse];

const RESPONSE_OPTIONS: readonly { id: ThreatResponseType; labelKey: TranslationKey }[] = [
  { id: ThreatResponse.FIGHT, labelKey: 'ui.threat.fightBack' },
  { id: ThreatResponse.FLEE, labelKey: 'ui.threat.runAway' },
  { id: ThreatResponse.IGNORE, labelKey: 'ui.threat.ignoreThreats' },
];

// Per-pawn draft state
const pawnDrafted = signal<Map<number, boolean>>(new Map());
const pawnThreatResponse = signal<Map<number, ThreatResponseType>>(new Map());

const isDrafted = ({ pawnId }: { pawnId: number }): boolean => {
  return pawnDrafted.value.get(pawnId) ?? false;
};

const getThreatResponse = ({ pawnId }: { pawnId: number }): ThreatResponseType => {
  return pawnThreatResponse.value.get(pawnId) ?? ThreatResponse.FIGHT;
};

const toggleDraft = ({ pawnId }: { pawnId: number }): void => {
  const next = new Map(pawnDrafted.value);
  next.set(pawnId, !isDrafted({ pawnId }));
  pawnDrafted.value = next;
};

const setThreatResponse = ({
  pawnId,
  response,
}: {
  pawnId: number;
  response: ThreatResponseType;
}): void => {
  const next = new Map(pawnThreatResponse.value);
  next.set(pawnId, response);
  pawnThreatResponse.value = next;
};

interface CombatPanelProps {
  pawn: PawnData;
}

export const CombatPanel = ({ pawn: _pawn }: CombatPanelProps) => {
  const styles = combatPanel();

  const ids = Array.from(selectedIds.value);
  const pawnId = ids[0];

  if (pawnId === undefined) {
    return null;
  }

  const drafted = isDrafted({ pawnId });
  const currentStance = selectedPawnCombatStance.value;
  const currentResponse = getThreatResponse({ pawnId });

  const handleDraftToggle = () => {
    toggleDraft({ pawnId });
  };

  const handleStanceChange = ({ stance }: { stance: CombatStanceType }) => {
    setCombatStance({ pawnId, stance });
  };

  const handleResponseChange = ({ response }: { response: ThreatResponseType }) => {
    setThreatResponse({ pawnId, response });
  };

  // Calculate combat stats (mock based on skills)
  const brawlingSkill = 2;
  const marksmanshipSkill = 3;
  const meleeDps = (brawlingSkill * 0.6 + 1.5).toFixed(1);
  const accuracy = Math.min(95, 45 + marksmanshipSkill * 8);

  return (
    <div class={styles.root()}>
      <div class={styles.title()}>{t('ui.panels.combat')}</div>

      {/* Draft Toggle */}
      <div class={styles.section()}>
        <button
          type="button"
          class={`${styles.draftButton()} ${drafted ? styles.draftedStyle() : styles.undraftedStyle()}`}
          onClick={handleDraftToggle}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4" aria-hidden="true">
            {drafted ? (
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            ) : (
              <path d="M6.92 5L5 7l4.5 4.5L7 14l1.5 1.5 2.5-2.5 4.5 4.5 2-1.92L5 3zm10.16 0L20 8l-3 3 3 3-2.08 2.08L13.5 11.5 11 14l-1.5-1.5 2.5-2.5-4.5-4.5L9.58 3 17.08 5z" />
            )}
          </svg>
          {drafted ? t('ui.combat.drafted') : t('ui.combat.draftForCombat')}
        </button>
        <p class="text-[9px] text-rs-parchment-dark text-center mt-1.5">
          {drafted ? t('ui.combat.awaitingOrders') : t('ui.combat.workingAutonomously')}
        </p>
      </div>

      {/* Combat Stance */}
      <div class={styles.stanceSection()}>
        <div class={styles.sectionLabel()}>{t('ui.labels.combatStance')}</div>
        {Object.values(CombatStance).map((stance) => {
          const isActive = currentStance === stance;
          const info = COMBAT_STANCE_INFO[stance];
          return (
            <button
              type="button"
              key={stance}
              class={`${styles.stanceOption()} ${isActive ? styles.stanceActive() : ''}`}
              onClick={() => handleStanceChange({ stance })}
            >
              <div class={`${styles.stanceRadio()} ${isActive ? styles.stanceRadioActive() : ''}`}>
                {isActive && <div class={styles.stanceDot()} />}
              </div>
              <div class={styles.stanceInfo()}>
                <div class={styles.stanceName()}>{info.name}</div>
                <div class={styles.stanceDesc()}>{info.description}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Threat Response */}
      <div class={styles.responseSection()}>
        <div class={styles.sectionLabel()}>{t('ui.labels.whenThreatened')}</div>
        {RESPONSE_OPTIONS.map((option) => {
          const isActive = currentResponse === option.id;
          return (
            <button
              type="button"
              key={option.id}
              class={`${styles.responseOption()} ${isActive ? styles.responseActive() : ''}`}
              onClick={() => handleResponseChange({ response: option.id })}
            >
              <div class={`${styles.stanceRadio()} ${isActive ? styles.stanceRadioActive() : ''}`}>
                {isActive && <div class={styles.stanceDot()} />}
              </div>
              <span class={styles.responseLabel()}>{t(option.labelKey)}</span>
            </button>
          );
        })}
      </div>

      {/* Combat Stats */}
      <div class={styles.statsSection()}>
        <div class={styles.sectionLabel()}>{t('ui.labels.combatStats')}</div>
        <div class={styles.statsRow()}>
          <span class={styles.statsLabel()}>{t('ui.combat.meleeDps')}</span>
          <span class={styles.statsValue()}>{meleeDps}</span>
        </div>
        <div class={styles.statsRow()}>
          <span class={styles.statsLabel()}>{t('ui.combat.rangedAccuracy')}</span>
          <span class={styles.statsValue()}>{accuracy}%</span>
        </div>
        <div class={styles.statsRow()}>
          <span class={styles.statsLabel()}>{t('ui.combat.dodge')}</span>
          <span class={styles.statsValue()}>8%</span>
        </div>
      </div>
    </div>
  );
};
